import {ChangeDetectionStrategy, Component, computed, inject, signal} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {NgOptimizedImage} from '@angular/common';
import {OphButtonModule} from 'src/app/shared/design/oph-button/oph-button.module';
import {OphXButtonComponent} from 'src/app/shared/design/oph-x-button/oph-x-button.component';
import {MessageService} from 'src/app/services/message.service';

export type ChallengeWarningDialogType = 'project' | 'challenge';
export type ChallengeWarningDialogAction = 'delete' | 'stop' | 'startOver';

export type ChallengeWarningDialogData = {
  type: ChallengeWarningDialogType;
  action: ChallengeWarningDialogAction;
  onConfirm: (callbacks: {onSuccess: () => void; onFailure: (err?: Error) => void}) => void;
};

@Component({
  selector: 'challenge-warning-dialog',
  standalone: true,
  imports: [OphButtonModule, OphXButtonComponent, NgOptimizedImage],
  templateUrl: './challenge-warning-dialog.component.html',
  styleUrl: './challenge-warning-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChallengeWarningDialogComponent {
  private dialogRef = inject(MatDialogRef<ChallengeWarningDialogComponent>);
  private messageService = inject(MessageService);
  data: ChallengeWarningDialogData = inject(MAT_DIALOG_DATA);

  loading = signal(false);

  title = computed(() => {
    if (this.data.action === 'startOver') return 'Restart this challenge?';
    if (this.data.action === 'stop') return 'Stop this challenge?';
    return this.data.type === 'project' ? 'Delete this project?' : 'Delete this challenge?';
  });

  message = computed(() => {
    if (this.data.action === 'startOver' || this.data.action === 'stop')
      return 'Your past cycle information will no longer be available.';
    return this.data.type === 'project'
      ? 'All project data will be permanently deleted.'
      : 'All challenge data will be permanently deleted.';
  });

  confirmButtonText = computed(() => {
    if (this.data.action === 'startOver') return 'Restart Challenge';
    if (this.data.action === 'stop') return 'Stop Challenge';
    return this.data.type === 'project' ? 'Delete Project' : 'Delete Challenge';
  });

  onConfirm(): void {
    this.loading.set(true);
    this.data.onConfirm({
      onSuccess: () => this.dialogRef.close(true),
      onFailure: (err?: Error) => {
        this.loading.set(false);
        this.messageService.add(err?.message || 'An error occurred.', 'error');
      },
    });
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}
